import Layout from "components/layout";
import { Menu } from "components/templates/Menu";
import { MenuNav } from "features/menu/components/MenuNav";
import { CvFromChatToSubscriptionPocProvider } from "features/menu/context/CvFromChatToSubscriptionPocProvider";
import { MenuProvider } from "features/menu/context/MenuProvider";
import { useCvFromChatToSubscriptionPoc } from "features/menu/hooks/useCvFromChatToSubscriptionPoc";
import { useMenuContext } from "features/menu/hooks/useMenuContext";
import { useFeatureFlags } from "features/shared/hooks/useFeatureFlags";
import { useSubscriptionList } from "features/subscriptions/hooks/useSubscriptionList";
import { GetStaticPaths, GetStaticProps } from "next";
import { NextSeo, NextSeoProps } from "next-seo";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { FC, ReactNode } from "react";
import { repository } from "repositories";
import { useFortune, useMe } from "repositories/hooks";
import { Button } from "shared/components/atoms/Button";
import { NavigationBase } from "shared/components/atoms/Navigation";
import { useIntersection } from "shared/hooks/useIntersection";
import { useScroll } from "shared/hooks/useScroll";
import { Data, Fortune } from "shared/types";
import { handleServerSideError } from "shared/utils/ssr";
import { SWRConfig } from "swr";
import tw from "twin.macro";
import { MetaTag } from "utils/seo";
import getNextSeoProps from "utils/ssr/getNextSeoProps";

const ViewItemTracker = dynamic(
  async () => (await import("shared/lib/criteo/components")).ViewItemTracker
);

interface Props {
  fallback: Record<string, Fortune>;
  nextSeoProps: NextSeoProps;
}

const FortuneDetail = ({ fallback, nextSeoProps }: Props) => {
  const { menu, isLoading: isLoadingMenu } = useMenuContext();
  const { me, isLoading: isLoadingMe } = useMe();

  const isLoading = isLoadingMenu || isLoadingMe;

  return (
    <SWRConfig value={{ fallback }}>
      <NextSeo {...nextSeoProps} />
      <Menu />
      {!isLoading && <ViewItemTracker id={menu?.id} email={me?.email} />}
    </SWRConfig>
  );
};

const Nav = () => {
  const { menu, mutate } = useMenuContext();
  const { isCvFromChatToSubscriptionPocEnabled, intersection } =
    useCvFromChatToSubscriptionPoc();
  const { scrollTo } = useScroll();

  const handleScrollToCvFromChatToSubscriptionPocElement = () => {
    scrollTo("CvFromChatToSubscriptionPocElement", { offset: -80 });
  };

  return isCvFromChatToSubscriptionPocEnabled ? (
    <NavigationBase
      tw="px-3 py-2"
      css={
        intersection
          ? tw`animate-slide-out-down animation-fill-forwards`
          : tw`animate-slide-in-up`
      }
    >
      <Button
        tw="bg-theme w-full rounded h-[52px]"
        onClick={handleScrollToCvFromChatToSubscriptionPocElement}
      >
        鑑定に進む
      </Button>
    </NavigationBase>
  ) : (
    <MenuNav menu={menu} onSuccessSubscribeNotification={mutate} />
  );
};

const MenuLayout: FC<{}> = ({ children }) => {
  const { query } = useRouter();

  const { fortune, isLoading, ...rest } = useFortune(query.id);
  const { isCvFromChatToSubscriptionPocEnabled } = useFeatureFlags();
  const { subscriptions } = useSubscriptionList();

  const [intersection, ref] = useIntersection<HTMLDivElement>();

  const canPoc =
    isCvFromChatToSubscriptionPocEnabled &&
    (process.env.NEXT_PUBLIC_APP_ENV === "production"
      ? fortune?.id === 3610
      : true) &&
    subscriptions.length === 0;

  return (
    <MenuProvider menu={fortune} isLoading={isLoading} {...rest}>
      <CvFromChatToSubscriptionPocProvider
        isCvFromChatToSubscriptionPocEnabled={canPoc}
        intersection={intersection}
        intersectionRef={ref}
      >
        <Layout nav={<Nav />}>{children}</Layout>
      </CvFromChatToSubscriptionPocProvider>
    </MenuProvider>
  );
};

FortuneDetail.getLayout = (page: ReactNode) => {
  return <MenuLayout>{page}</MenuLayout>;
};

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    fallback: "blocking",
  };
};

export const getStaticProps: GetStaticProps = async (ctx) => {
  const { params } = ctx;

  try {
    const { path, data } = await repository.fortune.getObject(params.id);
    const { fortune } = await data();

    const tags = fortune.divinationTags.map((tag: Data) => tag.name).join(",");

    const nextSeoProps = getNextSeoProps(ctx, {
      title: MetaTag.fortunesId.title(
        fortune.title,
        fortune.fortuneTeller.name
      ),
      description: MetaTag.fortunesId.description(fortune.content),
      url: `https://miror.jp/fortunes/${params.id}`,
      keywords: MetaTag.fortunesId.keywords(fortune.fortuneTeller.name, tags),
      options: {
        noindex: fortune.noindex,
        openGraph: {
          images: [
            { url: fortune.imageUrl, width: 800, height: 600 },
            { url: fortune.imageProcessedUrl, width: 800, height: 600 },
          ],
        },
      },
    });

    return {
      props: {
        fallback: {
          [path]: fortune,
        },
        nextSeoProps,
      },
    };
  } catch (error) {
    return handleServerSideError(error);
  }
};

export default FortuneDetail;
